import { useEffect } from 'react'

import { Select } from 'slab'

type BrokersMembersPlanYearSelectorProps = {
  isLoading: boolean
  selectedPlanYear?: number
  setSelectedPlanYear: (planYear: number) => void
  availablePlanYears: number[]
}

const BrokersMembersPlanYearSelector = ({
  isLoading,
  selectedPlanYear,
  setSelectedPlanYear,
  availablePlanYears,
}: BrokersMembersPlanYearSelectorProps) => {
  // Handle auto-selection when availablePlanYears change and no plan year is selected
  useEffect(() => {
    // Only proceed if we have available plan years
    if (availablePlanYears.length === 0) {
      return
    }

    // If we have a selected plan year, validate it's still available
    if (selectedPlanYear && availablePlanYears.includes(selectedPlanYear)) {
      return
    }

    // Check for selected_year query parameter in URL first
    const urlParams = new URLSearchParams(window.location.search)
    const urlSelectedYear = urlParams.get('selected_year')

    if (urlSelectedYear) {
      const parsedYear = parseInt(urlSelectedYear, 10)

      // Validate that the URL parameter exists in available plan years
      if (!isNaN(parsedYear) && availablePlanYears.includes(parsedYear)) {
        setSelectedPlanYear(parsedYear)
        return
      }
    }

    // Fallback: auto-select the most recent plan year (highest number)
    const mostRecentPlanYear = Math.max(...availablePlanYears)
    setSelectedPlanYear(mostRecentPlanYear)
  }, [availablePlanYears, setSelectedPlanYear]) // Removed selectedPlanYear from dependencies to prevent loops

  // Sync selectedPlanYear changes back to URL
  useEffect(() => {
    if (selectedPlanYear) {
      const urlParams = new URLSearchParams(window.location.search)
      urlParams.set('selected_year', selectedPlanYear.toString())

      // Update URL without causing a page reload
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`
      window.history.replaceState({}, '', newUrl)
    }
  }, [selectedPlanYear])

  if (isLoading || !selectedPlanYear || availablePlanYears.length === 0) {
    return (
      <div className='flex flex-col space-y-1'>
        <div className='text-sm font-medium text-gray-700'>Plan year</div>
        <div className='w-24 h-9 bg-gray-200 animate-pulse rounded-md' data-testid='loading-skeleton' />
      </div>
    )
  }

  return (
    <div>
      <Select
        label='Plan year'
        value={selectedPlanYear?.toString() || ''}
        onChange={(e) => {
          const newPlanYear = Number(e.target.value)
          setSelectedPlanYear(newPlanYear)
        }}
      >
        {availablePlanYears.map((planYear) => (
          <option key={planYear} value={planYear}>
            {planYear}
          </option>
        ))}
      </Select>
    </div>
  )
}

export default BrokersMembersPlanYearSelector
